import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'react-toastify';
import { administrationService } from '../../services/api/administrationService';
import type { AdministrationFormData } from '../../types/administration.types';

const AdministrationForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<AdministrationFormData>({
    office_type: 'secretariat',
    serial_number: 1,
    name: '',
    designation: '',
    office_address: '',
    contact: '',
    email: '',
    is_active: true,
    display_order: 0,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const officeTypes = [
    { value: 'secretariat', label: 'Secretariat' },
    { value: 'head_office', label: 'Head Office' },
  ];

  useEffect(() => {
    if (isEditMode && id) {
      fetchOfficer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  const fetchOfficer = async () => {
    try {
      setLoading(true);
      console.log('Fetching officer for ID:', id);
      const response = await administrationService.getById(Number(id));
      console.log('Response received:', response);

      if (!response.data) {
        throw new Error('No data received from server');
      }

      const officer = response.data;
      console.log('Setting form data:', officer);

      setFormData({
        office_type: officer.office_type,
        serial_number: officer.serial_number,
        name: officer.name,
        designation: officer.designation,
        office_address: officer.office_address,
        contact: officer.contact || '',
        email: officer.email || '',
        is_active: officer.is_active,
        display_order: officer.display_order,
      });

      toast.success('Officer loaded successfully');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch officer';
      toast.error(errorMessage);
      console.error('Error fetching officer:', error);
      console.error('Error details:', error.response);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.designation.trim()) {
      newErrors.designation = 'Designation is required';
    }

    if (!formData.office_address.trim()) {
      newErrors.office_address = 'Office address is required';
    }

    if (formData.serial_number < 1) {
      newErrors.serial_number = 'Serial number must be greater than 0';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setLoading(true);
      if (isEditMode && id) {
        await administrationService.update(Number(id), formData);
        toast.success('Officer updated successfully');
      } else {
        await administrationService.create(formData);
        toast.success('Officer created successfully');
      }
      navigate('/administration');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save officer';
      toast.error(errorMessage);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Loading State */}
      {loading && isEditMode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-700">Loading officer...</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/administration')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? 'Edit Officer' : 'Add New Officer'}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {isEditMode ? 'Update administration officer details' : 'Create new administration officer'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="space-y-6">
          {/* Office Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Office Type <span className="text-red-500">*</span>
            </label>
            <select
              name="office_type"
              value={formData.office_type}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.office_type ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              {officeTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {errors.office_type && <p className="mt-1 text-sm text-red-500">{errors.office_type}</p>}
          </div>

          {/* Serial Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Serial Number <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="serial_number"
              value={formData.serial_number}
              onChange={handleChange}
              min="1"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.serial_number ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="1"
            />
            {errors.serial_number && <p className="mt-1 text-sm text-red-500">{errors.serial_number}</p>}
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Sri/Smt. Full Name"
            />
            {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
          </div>

          {/* Designation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Designation <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.designation ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Minister / Secretary / Engineer-in-Chief"
            />
            {errors.designation && <p className="mt-1 text-sm text-red-500">{errors.designation}</p>}
          </div>

          {/* Office Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Office Address <span className="text-red-500">*</span>
            </label>
            <textarea
              name="office_address"
              value={formData.office_address}
              onChange={handleChange}
              rows={3}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.office_address ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Complete office address with floor, building, colony, city, pincode"
            />
            {errors.office_address && <p className="mt-1 text-sm text-red-500">{errors.office_address}</p>}
          </div>

          {/* Contact */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number</label>
            <input
              type="text"
              name="contact"
              value={formData.contact}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Phone number (optional)"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="email@telangana.gov.in (optional)"
            />
            {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
          </div>

          {/* Display Order */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
            <input
              type="number"
              name="display_order"
              value={formData.display_order}
              onChange={handleChange}
              min="0"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0"
            />
            <p className="mt-1 text-sm text-gray-500">Lower numbers appear first</p>
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
              Active (visible on administration page)
            </label>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/administration')}
            className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : isEditMode ? 'Update Officer' : 'Create Officer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdministrationForm;